import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Refresh user data every 30 seconds when authenticated
const REFRESH_INTERVAL = 30000;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  // Initialize token from localStorage on mount
  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    if (storedToken) {
      setToken(storedToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUser = useCallback(async () => {
    try {
      // Cache-buster + no-store to defeat any edge/CDN caching of auth-me responses
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' },
        params: { _t: Date.now() },
      });
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      // Only clear token on 401 Unauthorized errors, not on network issues.
      // Retry once before clearing — protects against transient CDN/network 401s
      // that were causing users (especially admins) to get silently logged out.
      if (error.response?.status === 401) {
        try {
          await new Promise(r => setTimeout(r, 1200));
          const retry = await axios.get(`${API_URL}/api/auth/me`, {
            headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' },
            params: { _t: Date.now() },
          });
          setUser(retry.data);
          return;
        } catch (retryErr) {
          if (retryErr.response?.status === 401) {
            console.warn('Session cleared: /auth/me returned 401 twice');
            localStorage.removeItem('token');
            delete axios.defaults.headers.common['Authorization'];
            setToken(null);
            setUser(null);
          }
        }
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch user when token changes
  useEffect(() => {
    if (token) {
      fetchUser();
    }
  }, [token, fetchUser]);

  // Periodic refresh of user data (for credit updates, etc.)
  useEffect(() => {
    if (!token) return;

    const interval = setInterval(() => {
      fetchUser();
    }, REFRESH_INTERVAL);

    // Also refresh on window focus (when user returns to tab)
    const handleFocus = () => {
      fetchUser();
    };
    window.addEventListener('focus', handleFocus);

    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', handleFocus);
    };
  }, [token, fetchUser]);

  const login = async (email, password) => {
    // Auto-retry once on transient failures (cold-start, brief DB blip, network hiccup).
    // Users kept seeing "Server error" on prod when the container was warming up —
    // a single retry after ~1.5s almost always succeeds and they never see the error.
    const attempt = async () => axios.post(
      `${API_URL}/api/auth/login`,
      { email, password },
      { timeout: 25000 }
    );

    let response;
    try {
      response = await attempt();
    } catch (err) {
      const isTransient = (
        !err.response
        || err.response.status >= 500
        || err.code === 'ECONNABORTED'
        || err.message === 'Network Error'
      );
      if (isTransient) {
        await new Promise(r => setTimeout(r, 1500));
        response = await attempt();
      } else {
        throw err;
      }
    }

    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const signup = async (email, password, name, phone) => {
    const response = await axios.post(`${API_URL}/api/auth/signup`, { email, password, name, phone });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  };

  const updateUser = (updates) => {
    setUser(prev => ({ ...prev, ...updates }));
  };

  const refreshUser = async () => {
    if (token) {
      await fetchUser();
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      login,
      signup,
      logout,
      updateUser,
      refreshUser,
      isAuthenticated: !!user,
      isCoach: user?.role === 'coach' || user?.role === 'admin',
      isAdmin: user?.role === 'admin',
      isStreamManager: user?.role === 'admin' || user?.is_stream_manager === true
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
